import type { MarketingAudience } from '@otw/models/leads/marketingCampaign'
import {
  cloneAudienceFilterGroup,
  emptyAudienceFilter,
  parseAudienceFilterGroup,
  parseReplyToUserIds,
} from '@otw/models/leads/marketingCampaign'
import { db } from '@otw/models/db'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { addDoc, deleteDoc, doc, getDoc, getDocs, setDoc } from 'firebase/firestore'
import { useAuth } from 'contexts/AuthContext'
import { useSnackbar } from '@/hooks/common/useSnackbar'

export const marketingAudiencesQueryKey = ['marketing_audiences'] as const

export interface CreateMarketingAudienceInput {
  name: string
  filter: MarketingAudience['filter']
  includedContactIds?: string[]
  excludedContactIds?: string[]
}

export interface UpdateMarketingAudienceInput {
  id: string
  name: string
  filter: MarketingAudience['filter']
  includedContactIds?: string[]
  excludedContactIds?: string[]
}

const sortAudiences = (audiences: MarketingAudience[]): MarketingAudience[] =>
  [...audiences].sort((a, b) => a.name.localeCompare(b.name, 'en'))

export const useMarketingAudiences = () => {
  const { userInfo } = useAuth()
  const { showSuccess, showError } = useSnackbar()
  const queryClient = useQueryClient()

  const {
    data: audiences,
    isLoading,
    error,
  } = useQuery({
    queryKey: marketingAudiencesQueryKey,
    queryFn: async () => {
      const snapshot = await getDocs(db.marketing_audiences)
      return sortAudiences(snapshot.docs.map((d) => d.data()))
    },
    staleTime: 1000 * 60 * 2,
  })

  const createMutation = useMutation({
    mutationFn: async (input: CreateMarketingAudienceInput) => {
      const uid = userInfo?.id
      if (!uid) throw new Error('You must be signed in.')
      const now = new Date()
      const payload: MarketingAudience = {
        id: '',
        name: input.name.trim(),
        filter: cloneAudienceFilterGroup(parseAudienceFilterGroup(input.filter)),
        includedContactIds: parseReplyToUserIds(input.includedContactIds),
        excludedContactIds: parseReplyToUserIds(input.excludedContactIds),
        createdByUserId: uid,
        createdAt: now,
        updatedAt: now,
      }
      const docRef = await addDoc(db.marketing_audiences, payload)
      return docRef.id
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: marketingAudiencesQueryKey })
      showSuccess('Saved audience created')
    },
    onError: () => showError('Could not save audience'),
  })

  const updateMutation = useMutation({
    mutationFn: async (input: UpdateMarketingAudienceInput) => {
      const ref = doc(db.marketing_audiences, input.id)
      const snap = await getDoc(ref)
      const current = snap.data()
      if (!current) throw new Error('Saved audience not found')
      const merged: MarketingAudience = {
        ...current,
        name: input.name.trim(),
        filter: cloneAudienceFilterGroup(parseAudienceFilterGroup(input.filter)),
        includedContactIds: parseReplyToUserIds(input.includedContactIds),
        excludedContactIds: parseReplyToUserIds(input.excludedContactIds),
        updatedAt: new Date(),
      }
      await setDoc(ref, merged)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: marketingAudiencesQueryKey })
      showSuccess('Saved audience updated')
    },
    onError: (err) =>
      showError(err instanceof Error ? err.message : 'Could not update audience'),
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await deleteDoc(doc(db.marketing_audiences, id))
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: marketingAudiencesQueryKey })
      showSuccess('Saved audience deleted')
    },
    onError: () => showError('Could not delete audience'),
  })

  return {
    audiences: audiences ?? [],
    isLoading,
    error: error instanceof Error ? error : error ? new Error(String(error)) : null,
    createAudience: (input: CreateMarketingAudienceInput) =>
      createMutation.mutateAsync(input),
    updateAudience: (input: UpdateMarketingAudienceInput) =>
      updateMutation.mutateAsync(input),
    deleteAudience: (id: string) => deleteMutation.mutateAsync(id),
    isSaving:
      createMutation.isPending || updateMutation.isPending || deleteMutation.isPending,
    emptyFilter: emptyAudienceFilter,
  }
}
